import { z } from 'zod';
import { API_VERSION, semverSchema } from './version';
import { minAppVersionByPlatformSchema } from './min-app-version';
import { assetUrlSchema, httpsUrlSchema, tenantCodeSchema } from './common';

export const tenantStatusSchema = z.enum(['active', 'maintenance', 'suspended', 'disabled']);
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

export const directoryTenantSchema = z.object({
  code: tenantCodeSchema,
  name: z.string().min(1).max(120),
  tenantUrl: httpsUrlSchema,
  logoUrl: assetUrlSchema.nullable(),
  status: tenantStatusSchema,
});
export type DirectoryTenant = z.infer<typeof directoryTenantSchema>;

/** Credit line under the app's sign-in screen until central says otherwise (DECISIONS 2026-10-08). */
export const DEFAULT_LOGIN_CREDIT_LINE = 'TeachPartner';
export const CREDIT_LINE_MAX = 80;

/**
 * App-wide sign-in credit line (central env LOGIN_PAGE_CREDIT_LINE). Plain text only: trimmed,
 * 0-80 characters, no control characters, angle brackets or URLs. Empty = show nothing.
 */
export const creditLineSchema = z
  .string()
  .trim()
  .max(CREDIT_LINE_MAX)
  // eslint-disable-next-line no-control-regex
  .refine((v) => !/[\u0000-\u001f\u007f<>]/.test(v), 'Plain text only (no markup)')
  .refine((v) => !/:\/\//.test(v), 'Plain text only (no links)');

/** App-wide settings central serves to every app before sign-in. Holds no school data. */
export const directoryAppSchema = z.object({ creditLine: creditLineSchema });
export type DirectoryApp = z.infer<typeof directoryAppSchema>;

/**
 * GET https://{CENTRAL_API_HOST}/v1/directory (brief §5.1). `app` is optional (additive,
 * apiVersion 1): an older central omits it and the app uses its bundled defaults.
 */
export const directorySchema = z.object({
  apiVersion: z.literal(API_VERSION),
  minAppVersion: semverSchema,
  /** Per-platform minimum (additive, review S7); see effectiveMinAppVersion. */
  minAppVersionByPlatform: minAppVersionByPlatformSchema.optional(),
  tenants: z.array(directoryTenantSchema),
  app: directoryAppSchema.optional(),
});
export type Directory = z.infer<typeof directorySchema>;

/** Tenants the picker shows: everything except `disabled`. */
export function visibleTenants(directory: Directory): DirectoryTenant[] {
  return directory.tenants.filter((t) => t.status !== 'disabled');
}
